import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, PenLine } from "lucide-react";

/**
 * SignaturePad — smooth canvas signature with touch + mouse support.
 * onChange(dataUrl | "")  fires with a base64 PNG when strokes exist, or "" when cleared.
 */
export default function SignaturePad({ value, onChange, label = "Tanda tangan", testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasStroke, setHasStroke] = useState(!!value);

  const getCtx = () => canvasRef.current.getContext("2d");

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    const dpr = window.devicePixelRatio || 1;
    const w = parent.clientWidth;
    const h = 180;
    c.width = w * dpr;
    c.height = h * dpr;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111827";
    // If restoring an image
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, h);
      img.src = value;
    }
  };

  useEffect(() => {
    resize();
    const onR = () => resize();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pointFrom = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pointFrom(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pointFrom(e);
    const ctx = getCtx();
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasStroke) setHasStroke(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasStroke(false);
    if (onChange) onChange("");
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5">
          <PenLine className="h-3.5 w-3.5" /> {label}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={clear} className="gap-1 h-8" data-testid={`${testid}-clear`}>
          <Eraser className="h-3.5 w-3.5" /> Bersihkan
        </Button>
      </div>
      <div className="rounded-md border border-border/70 bg-white">
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="w-full touch-none cursor-crosshair"
        />
      </div>
      <div className="text-[11px] text-muted-foreground">
        {hasStroke ? "Sudah ditandatangani. Tekan 'Bersihkan' untuk mengulang." : "Gambar tanda tangan di kotak di atas."}
      </div>
    </div>
  );
}
